import React, { useRef, useEffect, useState } from "react";

import { useWebSocketChat } from "../../hooks/useWebSocketChat";

import { ChatMessage } from "./ChatMessage";
import { ChatErrorBoundary } from "./ChatErrorBoundary";
import { ChatTypingIndicator } from "./ChatTypingIndicator";

interface BaseChatProps {
  endpoint: string;
  queryParams?: Record<string, string>;
}

export const BaseChat: React.FC<BaseChatProps> = ({
  endpoint,
  queryParams,
}) => {
  const {
    messages,
    inputMessage,
    setInputMessage,
    status,
    error,
    isTyping,
    handleSend,
    handleKeyPress,
    connectWebSocket,
    currentTaskNotification,
  } = useWebSocketChat({ endpoint, queryParams });

  const [taskNotifications, setTaskNotifications] = useState<Array<any>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, taskNotifications]);

  useEffect(() => {
    if (currentTaskNotification) {
      const isDuplicate = messages.some(
        (msg) => msg.raw_text === currentTaskNotification.raw_text
      );

      if (isDuplicate) {
        return;
      }

      setTaskNotifications(prev => {
        if (prev.length > 0 && prev[0].raw_text === currentTaskNotification.raw_text) {
          return prev;
        }
        const newNotifications = [...prev, currentTaskNotification].slice(-2);
        return newNotifications;
      });
    }
  }, [currentTaskNotification, messages]);

  // Handle notification removal after animation ends
  const handleAnimationEnd = (index: number, event: React.AnimationEvent, modifierClassName: string) => {
    if (modifierClassName === "final" && event.animationName === 'fadeOut') {
      setTaskNotifications([])
    }
  };

  const displayStatus = () => {
    return messages.length === 0 ? "connecting" : status;
  };

  const renderConnectionStatus = () => {
    switch (displayStatus()) {
      case "connecting":
        return <ChatTypingIndicator />;
      case "disconnected":
        return (
          <div className="notification is-warning">
            <button className="delete" type="button" />
            Disconnected.{" "}
            <button
              className="button is-small is-ghost"
              type="button"
              onClick={connectWebSocket}
            >
              Click to reconnect
            </button>
          </div>
        );
      case "error":
        return (
          <div className="notification is-danger">
            <button className="delete" type="button" />
            {error || "Connection error."}{" "}
            <button
              className="button is-small is-ghost"
              type="button"
              onClick={connectWebSocket}
            >
              Click to retry
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const renderTaskNotifications = () => {
    if (taskNotifications.length === 0) return null;

    return (
      <div className="chat-task-notifications">
        {taskNotifications.map((notification, index) => {
          const isFinal = notification.message_type === "task_notification_final";
          const isOldest = taskNotifications.length > 1 && index === 0;

          let modifierClassName = "newest";
          if (isFinal) {
            modifierClassName = "final";
          } else if (isOldest) {
            modifierClassName = "oldest";
          }
          const className = `chat-task-notification ${modifierClassName}`;

          return (
            <div
              key={`${index}-${notification.raw_text}`}
              className={className}
              onAnimationEnd={(e) => handleAnimationEnd(index, e, modifierClassName)}
            >
              <div className="chat-task-notification__content">
                {notification.raw_text}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ChatErrorBoundary>
      <div className="chat-body-container">
        <div className="chat-container">
          <div className="chat-messages">
            {renderConnectionStatus()}
            {messages.map((message, index) => (
              <ChatMessage key={index} message={message} />
            ))}
            {isTyping && <ChatTypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {taskNotifications.length > 0 && renderTaskNotifications()}

          <div className="chat-input">
            <div className="chat-input__container">
              <textarea
                className="chat-input__textarea"
                disabled={status !== "connected"}
                placeholder={status !== "connected" ? "" : "Type your message..."}
                rows={3}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              <button
                className="chat-input__button"
                disabled={!inputMessage.trim() || status !== "connected"}
                type="button"
                onClick={handleSend}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </ChatErrorBoundary>
  );
};
